import { useState, useCallback } from 'react';
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
} from '@tanstack/react-router';
import { BackButton } from '@/components/ui/backButton';
import { StickySection } from '@/components/assessment/stickySection';
import { FormSection, AnswerValue } from '@/components/assessment/formSection';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getQuestionnaireQueryOptions } from '@/api/queries/assessment';
import { getPatientQueryOptions } from '@/api/queries/patients';
import { useTranslation } from 'react-i18next';
import { ConfirmAnswersDialog } from '@/components/assessment/confirmAnswersDialog';
import { UnansweredQuestionsDialog } from '@/components/assessment/unansweredQuestionsDialog';
import { ErrorComponent } from '@/components/generalError';
import { useSubmitQuestionnaireMutation } from '@/api/mutations/questionnaire';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/types/routes';

export const Route = createFileRoute(
  '/_app/patients/$patientId/phases/$phaseId/assessments/$assessmentId/questionnaire',
)({
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const params = Route.useParams();
  const { patientId, phaseId, assessmentId } = params;
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const { data: patient } = useSuspenseQuery({
    ...getPatientQueryOptions(patientId),
    staleTime: 1000 * 60 * 60,
  });
  const activeSubject = patient.subjects?.find(subject => subject.isActive);

  if (!activeSubject) {
    throw new Error(t('errors.noActiveSubject', { ns: 'common' }));
  }

  const { data: form } = useSuspenseQuery(
    getQuestionnaireQueryOptions({
      subjectId: activeSubject.id,
      phaseId,
      assessmentId,
    }),
  );
  const [ currentPageIndex, setCurrentPageIndex ] = useState(0);
  const [ answers, setAnswers ] = useState<
    Record<number, Record<string, AnswerValue>>
  >({});
  const [ showConfirmDialog, setShowConfirmDialog ] = useState(false);
  const [ showUnansweredDialog, setShowUnansweredDialog ] = useState(false);
  const submitMutation = useSubmitQuestionnaireMutation();

  const currentPage = form.pages[currentPageIndex];
  const isLastPage = currentPageIndex === form.pages.length - 1;
  const isFirstPage = currentPageIndex === 0;

  const handlePageAnswers = useCallback(
    (pageAnswers: (typeof answers)[0]) => {
      setAnswers(prev => ({ ...prev, [currentPageIndex]: pageAnswers }));
    },
    [ currentPageIndex ],
  );

  const handleNext = (pageAnswers: (typeof answers)[0]) => {
    handlePageAnswers(pageAnswers);
    if (!isLastPage) setCurrentPageIndex(idx => idx + 1);
  };

  const handleBack = (pageAnswers: (typeof answers)[0]) => {
    handlePageAnswers(pageAnswers);
    if (!isFirstPage) setCurrentPageIndex(idx => idx - 1);
  };

  const handleSubmit = () => {
    const parsedAnswers = Object.values(answers).flatMap(pageAnswers =>
      Object.entries(pageAnswers).map(([ questionId, value ]) => ({
        question_id: questionId,
        value,
      })),
    );

    submitMutation.mutate(
      {
        subjectId: activeSubject.id,
        phaseId,
        assessmentId,
        responses: parsedAnswers,
      },
      {
        onSuccess: () => {
          toast({
            description: t('toasts.formSubmitted', { ns: 'common' }),
          });
          setShowConfirmDialog(false);
          setShowUnansweredDialog(false);
          setCurrentPageIndex(0);
          setAnswers({});
          void navigate({
            to: routes.assessmentFormResponse,
            replace: true,
            params: {
              patientId,
              phaseId,
              assessmentId,
            },
          });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: t('toasts.errors.somethingWentWrong', { ns: 'common' }),
            description: t('toasts.errors.couldntSubmitForm', { ns: 'common' }),
          });
        },
        onSettled: () => {
          setShowConfirmDialog(false);
          setShowUnansweredDialog(false);
        },
      },
    );
  };

  const handleFormSubmit = (pageAnswers: (typeof answers)[0]) => {
    handlePageAnswers(pageAnswers);
    const allAnswers = { ...answers, [currentPageIndex]: pageAnswers };

    const hasUnanswered = form.pages.some((page, i) =>
      page.questions.some((q) => {
        const pageAns = allAnswers[i];
        const answer = pageAns[q.id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return answer === undefined || answer === null || answer === '';
      }),
    );

    setShowConfirmDialog(!hasUnanswered);
    setShowUnansweredDialog(hasUnanswered);
  };

  const currentPageAnswers = answers[currentPageIndex] ?? {};

  return (
    <div className="relative flex flex-col flex-1">
      <div className="flex flex-col gap-2 mb-10">
        <div>
          <BackButton
            onClick={
              !canGoBack
                ? () =>
                  navigate({
                    to: routes.patientDetails,
                    params: { patientId },
                  })
                : undefined
            }
          />
        </div>
        <div className="flex flex-row items-center justify-between gap-6">
          <h1 className="text-text-primary-default text-3xl font-semibold">
            { form.name }
          </h1>
          <div className="flex flex-col justify-end text-end">
            <span className="text-body-md-semibold font-semibold">
              { activeSubject.protocol.name }
            </span>
            <span className="text-body-md-normal">
              { t('mrn', { ns: 'common', value: patient.externalId }) }
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[1fr_355px] gap-x-space-lg mb-24">
        <FormSection
          formTemplate={ currentPage }
          pageAnswers={ currentPageAnswers }
          onNext={ handleNext }
          onBack={ handleBack }
          onSubmit={ handleFormSubmit }
          currentPage={ currentPageIndex + 1 }
          totalPages={ form.pages.length }
        />
        <StickySection
          title={ t('assessment.instructions', { ns: 'patients' }) }
          bodyText={ form.description }
          footerNote={ t('assessment.footerNote', { ns: 'patients' }) }
        />
      </div>
      <ConfirmAnswersDialog
        open={ showConfirmDialog }
        onOpenChange={ setShowConfirmDialog }
        onSubmit={ handleSubmit }
        isSubmitting={ submitMutation.isPending }
      />
      <UnansweredQuestionsDialog
        open={ showUnansweredDialog }
        onOpenChange={ setShowUnansweredDialog }
        onSubmit={ handleSubmit }
        isSubmitting={ submitMutation.isPending }
      />
    </div>
  );
}
